import { h, Fragment } from "preact";
import { Card, Button, Space, Alert, Empty, Typography, Input } from 'antd';
import { SETTING_GROUPS } from './constants/settingsConfig';
import {useFPE} from "./hooks/useFPE.js";
import SettingsGroup from "./SettingsGroup.jsx";

const { Title, Text } = Typography;

const FPEPage = () => {
    const {
        selectedPersonId,
        personIdInput,
        setPersonIdInput,
        settings,
        loading,
        hasChanges,
        globalSettings,
        handleSettingChange,
        handleToggle,
        resetToDefault,
        saveAllChanges,
        applyPersonId,
        clearPersonSelection
    } = useFPE();

    const handlePersonIdChange = (e) => {
        setPersonIdInput(e.target.value);
    };

    const handleApplyPersonId = () => {
        applyPersonId();
    };

    const handleClearPerson = () => {
        clearPersonSelection();
    };

    const getTargetName = () => {
        if (selectedPersonId) {
            return `person ${selectedPersonId}`;
        }
        return "consumer";
    };

    if (loading.loadingGlobal) {
        return (
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
                <Card>
                    <div style={{ textAlign: 'center', padding: '48px' }}>
                        <Text>Loading global settings...</Text>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>Consumer Settings</Title>
                        <Text type="secondary">Configure security and verification settings for specific persons</Text>
                    </div>
                    {selectedPersonId && (
                        <Space>
                            {!globalSettings.turn_off_fpe && (
                                <Button
                                    icon="🔄"
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                    onClick={resetToDefault}
                                >
                                    Reset to Default
                                </Button>
                            )}
                            <Button
                                type="primary"
                                icon="💾"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={saveAllChanges}
                                loading={loading.saving}
                                disabled={!hasChanges}
                            >
                                Save Changes
                            </Button>
                        </Space>
                    )}
                </div>
            </div>
            {/* Person ID Input - Always visible when consumer is selected */}
            {(
                <Card style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <Title level={4} style={{ margin: 0, marginBottom: '8px' }}>
                                Enter Person ID
                            </Title>
                            <Text type="secondary">
                                You must specify a person ID to configure their security settings.
                            </Text>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <Text strong>Person ID:</Text>
                            <Input
                                placeholder="Enter person ID to configure settings"
                                value={personIdInput}
                                onChange={handlePersonIdChange}
                                style={{ width: '300px' }}
                                disabled={loading.loadingSettings}
                                onPressEnter={handleApplyPersonId}
                            />
                            <Button
                                type="primary"
                                onClick={handleApplyPersonId}
                                disabled={loading.loadingSettings || !personIdInput.trim()}
                                loading={loading.loadingSettings}
                            >
                                Load Settings
                            </Button>
                            {selectedPersonId && (
                                <Button
                                    onClick={handleClearPerson}
                                    disabled={loading.loadingSettings}
                                >
                                    Clear Selection
                                </Button>
                            )}
                        </div>

                        {selectedPersonId && (
                            <Alert
                                message={`Currently configuring settings for person: ${selectedPersonId}`}
                                type="info"
                                showIcon
                            />
                        )}
                    </div>
                </Card>
            )}

            {/* No Person Selected State */}
            {!selectedPersonId && (
                <Card>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <div>
                                <Title level={4}>No Person Selected</Title>
                                <Text type="secondary">
                                    Please enter a person ID above to load and configure their security settings.
                                </Text>
                            </div>
                        }
                    />
                </Card>
            )}
            {/* Unsaved Changes Warning */}
            {hasChanges && selectedPersonId && (
                <Alert
                    message={`You have unsaved changes for ${getTargetName()}`}
                    description="Don't forget to save your changes before switching to another person or leaving this page."
                    type="warning"
                    showIcon
                    closable
                    style={{ marginBottom: '24px' }}
                />
            )}

            {/* Settings Groups - Only show when person is selected */}
            {selectedPersonId && (
                <>
                    {Object.values(SETTING_GROUPS).map(group => (
                        <SettingsGroup
                            key={group.title}
                            group={group}
                            settings={settings}
                            globalSettings={globalSettings}
                            loading={loading}
                            onToggle={handleToggle}
                            onSettingChange={handleSettingChange}
                        />
                    ))}
                    {/* Footer Actions */}
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <Button
                                size="large"
                                onClick={handleClearPerson}
                                disabled={loading.saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                icon="💾"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={saveAllChanges}
                                loading={loading.saving}
                                disabled={!hasChanges}
                            >
                                Save All Changes
                            </Button>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
};

export default FPEPage;